import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import ActionsCancelNormalXsmall from '@strava/icons/ActionsCancelNormalXsmall';
import ActionsPlayNormalXsmall from '@strava/icons/ActionsPlayNormalXsmall';
import { CameraTarget } from '@strava/mre';

import { FLYOVER_PREVIEW_DURATION } from '../../constants';
import { getWorldBounds2FromLatLngArray } from '../../helpers/getWorldBounds2FromLatLngArray';
import { mergeCoordinatesAndElevation } from '../../helpers/mergeCoordinatesAndElevation';
import { useMRE } from '../../hooks/useMRE';
import { useMREState } from '../../hooks/useMREState';
import { FlyoverRenderer } from '../FlyoverRenderer/FlyoverRenderer';

import styles from './Flyover.module.scss';

interface FlyoverProps {
  polyline: Array<[number, number]>;
  elevation: Array<[number, number]>;
  isPreview?: boolean; // If true, the flyover will only show a preview
  onPreviewEnd?: () => void; // if isPreview, this callback will be called at end of the preview
}

export const Flyover = ({
  polyline,
  elevation,
  isPreview = false, // Default to false if not provided
  onPreviewEnd
}: FlyoverProps) => {
  const endTarget = useRef<CameraTarget>();
  // Use a ref to store the showPOIs state to use when the flyover completes
  // This is set to the current state when the flyover starts
  const showPOIsOnFlyoverCompleteRef = useRef<boolean | undefined>(undefined);

  const { terrainEngine, track, t } = useMRE();

  const { isFlyoverInProgress, setIsFlyoverInProgress, showPOIs, setShowPOIs } =
    useMREState();

  // Reset camera and content to original state
  const resetCameraAndContent = useCallback(() => {
    if (polyline && terrainEngine) {
      // Restore showPOIs state to how it was prior to the flyover
      showPOIsOnFlyoverCompleteRef.current &&
        setShowPOIs(showPOIsOnFlyoverCompleteRef.current);

      // Fly to the bounds of the polyline with a margin
      terrainEngine
        .getCamera()
        .flyToBounds(
          getWorldBounds2FromLatLngArray(polyline),
          0,
          0,
          0,
          { top: 0.05, right: 0.1, bottom: 0.05, left: 0.1 },
          undefined
        );
    }
  }, [polyline, terrainEngine, setShowPOIs, showPOIsOnFlyoverCompleteRef]);

  useEffect(() => {
    if (isFlyoverInProgress && isPreview) {
      // After FLYOVER_PREVIEW_DURATION (6 secs)
      const flyoverPreviewTimeoutId = setTimeout(() => {
        resetCameraAndContent(); // Reset camera and content to original state
        setIsFlyoverInProgress(false); // Exit flyover mode
        onPreviewEnd?.(); // Call callback if provided
      }, FLYOVER_PREVIEW_DURATION);

      return () => clearTimeout(flyoverPreviewTimeoutId); // Clear the timeout if the component unmounts
    }
  }, [
    isFlyoverInProgress,
    isPreview,
    resetCameraAndContent,
    setIsFlyoverInProgress,
    onPreviewEnd
  ]);

  // Handle user starting the flyover
  const handleStartFlyover = () => {
    if (terrainEngine) {
      // Set showPOIsOnFlyoverCompleteRef to the current showPOIs state
      // This will be used to restore the showPOIs state when the flyover completes
      showPOIsOnFlyoverCompleteRef.current = showPOIs;

      // Hide POIs during flyover
      setShowPOIs(false);

      // Set endTarget for flyover to the current camera target
      endTarget.current = terrainEngine.getCamera().getTarget();

      setIsFlyoverInProgress(true);

      track({
        action: 'screen_enter',
        element: 'flyover'
      });
    }
  };

  // Handle user canceling the flyover
  const handleCancelFlyover = useCallback(() => {
    // Reset camera and content to original state
    resetCameraAndContent();

    setIsFlyoverInProgress(false);

    track({
      action: 'cancel',
      element: 'flyover'
    });
  }, [track, setIsFlyoverInProgress, resetCameraAndContent]);

  // Format polyline and elevation data for flyover
  const flyoverPathData = useMemo(() => {
    return mergeCoordinatesAndElevation(polyline, elevation);
  }, [polyline, elevation]);

  if (!isFlyoverInProgress) {
    // Show flyover start button
    return (
      <button
        title={t('mre.flyover.start')}
        className={styles.flyoverButton}
        onClick={handleStartFlyover}
        data-testid="mre-start-flyover-button"
      >
        <ActionsPlayNormalXsmall />
      </button>
    );
  }

  if (isFlyoverInProgress) {
    return (
      <>
        {/* Render Flyover */}
        <FlyoverRenderer
          path={flyoverPathData}
          playbackSpeedFactor={1.6}
          endTarget={endTarget.current}
        />
        {/* Show flyover exit button */}
        <button
          title={t('mre.flyover.exit')}
          className={styles.cancelFlyoverButton}
          onClick={handleCancelFlyover}
          data-testid="mre-exit-flyover-button"
        >
          <ActionsCancelNormalXsmall />
        </button>
      </>
    );
  }

  return null;
};
